import type { OutboxEntry, Serializer } from "@norkron/web-sync-queue";
import { uuidv7 } from "@norkron/web-sync-queue";

/**
 * Temp→real id resolution (design §5.3) — the cross-entity ordering fix.
 *
 * A reply created while OFFLINE references its parent comment by a **client temp
 * id** (`tmp-<uuidv7>`) because the parent has no server id yet. Two seams:
 *
 *  - **Local (overlay):** the optimistic parent renders under its temp id, and
 *    the optimistic reply hangs off that same temp id (see `overlay.ts`).
 *  - **Wire (this serializer):** a `SyncQueueConfig.serializer` rewrites the
 *    payload **at send time** — every id field carrying a temp id is swapped to
 *    the real server id once the parent's `create` confirms. Because the drain is
 *    FIFO + serial, the parent's `applied` result always precedes the reply's
 *    send, so the `TempIdRegistry` is populated in time. An UNRESOLVED temp id is
 *    left in place (held back) rather than fabricating a real id — the item stays
 *    replayable, and a strict host can pre-filter dependent creates.
 *
 * This is pure elaborate glue over the existing `serializer` seam — no upstream
 * `web-sync-queue` change is needed for v1 (candidate FR only on the 2nd
 * consumer, design §12).
 */

/** The client temp-id prefix. A temp id is `tmp-<uuidv7>` — time-sortable. */
const TEMP_PREFIX = "tmp-";

/** Mint a fresh client temp id for a not-yet-persisted entity (a comment root). */
export function mintTempId(): string {
  return TEMP_PREFIX + uuidv7();
}

/** True iff `v` is a client temp id (`tmp-…`), never a real numeric server id. */
export function isTempId(v: unknown): v is string {
  return typeof v === "string" && v.startsWith(TEMP_PREFIX);
}

/**
 * The temp→real id map, populated as each offline `create` confirms with its
 * authoritative `server_row.id`. `queue.ts`'s fetch wrapper calls {@link record}
 * from the batch response; the {@link serializer} reads it at send time.
 */
export class TempIdRegistry {
  private readonly map = new Map<string, number>();

  /** Bind a temp id to the real server id its `create` was assigned. */
  record(tempId: string, realId: number): void {
    if (!isTempId(tempId)) return;
    this.map.set(tempId, realId);
  }

  /** The real id for `tempId`, or undefined while its create is still unresolved. */
  resolve(tempId: string): number | undefined {
    return this.map.get(tempId);
  }

  /** True once `tempId`'s create has confirmed. */
  has(tempId: string): boolean {
    return this.map.has(tempId);
  }

  /** Test/inspection helper: current binding count. */
  size(): number {
    return this.map.size;
  }
}

/**
 * The id fields a payload may carry that reference another entity by temp id.
 * `parent_id` is the reply→root ref; the list is the extension seam for future
 * create-then-reference shapes (design §5.3).
 */
const REF_FIELDS = ["parent_id"] as const;

/**
 * Build a `SyncQueueConfig.serializer` that rewrites temp id refs to real ids
 * using `registry`. A payload that is not a plain object (or carries no temp
 * refs) is returned untouched. A temp ref that is still UNRESOLVED is left as-is
 * (held back) — never coerced to a bogus id.
 */
export function createTempIdSerializer(
  registry: TempIdRegistry,
  refFields: readonly string[] = REF_FIELDS,
): Serializer {
  return {
    serialize(entry: OutboxEntry): unknown {
      const payload = entry.payload;
      if (payload === null || typeof payload !== "object") return payload;
      const src = payload as Record<string, unknown>;
      let out: Record<string, unknown> | null = null;
      for (const field of refFields) {
        const v = src[field];
        if (!isTempId(v)) continue;
        const real = registry.resolve(v);
        if (real === undefined) continue; // unresolved → hold the temp id in place
        out ??= { ...src };
        out[field] = real;
      }
      // `__tempId` is intentionally LEFT on the wire: the fetch wrapper reads it
      // from the outbound batch item to correlate this create's confirmation
      // (`server_row.id`) back to its temp id, populating the registry so a later
      // reply's `parent_id` resolves. The backend ignores unknown fields
      // (deny-by-default: it parses only the columns it owns).
      return out ?? payload;
    },
  };
}
